import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Box, DialogContent, Divider, Typography } from '@material-ui/core'

import { Yup, handleValidationError, validateWithSchema } from 'services/yup'
import { updateCollectItemRequest } from '@/store/modules/users/collections/reducer'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import { FormTextField } from '@/components/CoForm'

import { StyledForm } from './styles'
import CheckBox from '@/components/Checkbox'

const editCollectSchema = Yup.object({
  vpt_pallet: Yup.number()
    .typeError('Quantidade inválida')
    .required('Informe a quantidade'),
  vpt_qtd_caixas: Yup.number()
    .typeError('Quantidade inválida')
    .required('Informe a quantidade')
    .when('vpt_pallet', (palletAmount, schema) => {
      if (Number(palletAmount) === 0) {
        return schema.min(1, 'A quantidade minima de caixas é "1"')
      }
    }),
})

// ---------------------------------------------------------------------

export function CollectForm(props) {
  const { current, onClose, ...other } = props

  const formRef = useRef(null)

  const defaultValues = useMemo(
    () => ({
      vpt_pallet: current?.vpt_pallet || 0,
      vpt_qtd_caixas: current?.vpt_qtd_caixas || 0,
    }),
    [current]
  )

  const dispatch = useDispatch()

  const [enableBoxesAmountEdit, setEnableBoxesAmountEdit] = useState(true)
  const [enablePalletAmountEdit, setEnablePalletAmountEdit] = useState(true)
  const [includeBoxesOnShipment, setIncludeBoxesOnShipment] = useState(true)

  function handleSubmit(updateParams) {
    dispatch(updateCollectItemRequest({ current, updateParams }))

    onClose()
  }

  function handleSubmitForm() {
    formRef.current.setErrors({})

    const data = formRef.current.getValues()

    validateWithSchema(
      editCollectSchema,
      data,
      handleSubmit,
      handleValidationError(formRef.current)
    )
  }

  function handleChangePalletsAmount(event) {
    const value = event.target.value
    const palletsAmount = Number(value)
    const enableBoxesAmountEdit = palletsAmount === 0

    setEnableBoxesAmountEdit(enableBoxesAmountEdit)
  }

  function handleChangeBoxesAmount(event) {
    const value = event.target.value
    const boxes = Number(value)

    const enablePalletAmountEdit =
      boxes === 0 || boxes === defaultValues.vpt_qtd_caixas

    setEnablePalletAmountEdit(enablePalletAmountEdit)
  }

  function handleToogleIncludeBoxesOnShipment(_, checked) {
    setIncludeBoxesOnShipment(checked)

    const boxesAmount = checked ? defaultValues.vpt_qtd_caixas : 0
    formRef.current.setFieldValue('vpt_qtd_caixas', boxesAmount)

    setEnablePalletAmountEdit(true)
  }

  useEffect(() => {
    if (formRef.current) {
      formRef.current.setData(defaultValues)
      formRef.current.setErrors({})
    }

    const enableBoxesAmountEdit = defaultValues.vpt_pallet === 0

    setEnableBoxesAmountEdit(enableBoxesAmountEdit)
    setEnablePalletAmountEdit(!enableBoxesAmountEdit)
  }, [defaultValues])

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      title="Editar Quantidade"
      fullScreen={false}
      onClose={onClose}
      actions={
        <>
          <Button
            label="Cancelar"
            variant="outlined"
            color="primary"
            onClick={onClose}
          />

          <Button
            variant="contained"
            label="Salvar"
            color="primary"
            type="button"
            onClick={handleSubmitForm}
          />
        </>
      }
      {...other}
    >
      <DialogContent>
        <Box mb={3} minHeight={50}>
          {current && (
            <>
              <Typography variant="body2" color="textSecondary">
                {current?.cliente_nome}
              </Typography>

              <Typography variant="body1" color="primary">
                {current?.cod_pedido} / {current?.dsc_abreviado}
              </Typography>
            </>
          )}
        </Box>

        <StyledForm
          formRef={formRef}
          initialData={defaultValues}
          onSubmit={handleSubmit}
        >
          <FormTextField
            size="medium"
            label="Paletes"
            name="vpt_pallet"
            required
            type="number"
            inputProps={{
              min: 0,
              max: defaultValues.vpt_pallet,
            }}
            style={{ width: 120 }}
            onChange={handleChangePalletsAmount}
            //
            disabled={!enablePalletAmountEdit}
          />

          <Divider orientation="vertical" flexItem style={{ maxHeight: 50 }} />

          <Box display="flex" flexDirection="column" gridGap="0.5rem">
            <FormTextField
              size="medium"
              label="Caixas"
              name="vpt_qtd_caixas"
              required
              type="number"
              inputProps={{
                min: 0,
                max: defaultValues.vpt_qtd_caixas,
              }}
              style={{ width: 120 }}
              //
              disabled={!enableBoxesAmountEdit}
              onChange={handleChangeBoxesAmount}
            />

            <CheckBox
              label="Incluir caixas na coleta?"
              checked={includeBoxesOnShipment}
              onChange={handleToogleIncludeBoxesOnShipment}
            />
          </Box>
        </StyledForm>
      </DialogContent>
    </Dialog>
  )
}
